import { parseAccent, parsePosition, parseRole } from '../../lib/boardInvariants'
import type {
  GroupId,
  Player,
  PlayerGroup,
  RaidGroup,
  RaidPlayer,
  RaidRoom,
  Room,
} from '../../types'

// Re-export the wire-level node types so the slim composer can use them
// for `fetchRaidBoard`.
export interface RaidRoomNode {
  readonly id: number
  readonly stableId: string
  readonly guildId: number
  readonly raidId: number
  readonly stageId: number | null
  readonly name: string
  readonly accent: string
  readonly noPlayerLimit: boolean
  readonly orderIndex: number
}

export interface RaidGroupNode {
  readonly id: number
  readonly stableId: string
  readonly guildId: number
  readonly raidId: number
  readonly stageId: number | null
  readonly roomId: number
  readonly label: string
  readonly position: string
  readonly maxPlayers: number
  readonly collapsed: boolean
  readonly orderIndex: number
}

export interface RaidPlayerWithBuildsNode {
  readonly player: {
    readonly id: number
    readonly guildId: number
    readonly raidId: number
    readonly stageId: number | null
    readonly playerId: number | null
    readonly discordUserId: string
    readonly hasSignedIn: boolean
    readonly discordName: string
    readonly guildNickname: string
    readonly playerNickname: string
    readonly playerAccountName: string
    readonly inGameName: string
    readonly role: string
    readonly groupId: number
    readonly groupOrderIndex: number
    readonly mainWeapons: ReadonlyArray<string>
    readonly altWeapons: ReadonlyArray<string>
    readonly activeBuildIndex: number
    readonly tags: ReadonlyArray<RaidBoardTagNode>
  }
  readonly builds: ReadonlyArray<{
    readonly id: number
    readonly orderIndex: number
    readonly weaponSlugs: ReadonlyArray<string>
  }>
}

export interface RaidBoardTagNode {
  readonly id: number
  readonly guildId: number
  readonly name: string
  readonly color: string
}

export interface RaidBoardPayloadShape {
  board?: {
    rooms: ReadonlyArray<RaidRoomNode>
    groups: ReadonlyArray<RaidGroupNode>
    players: ReadonlyArray<RaidPlayerWithBuildsNode>
  }
}

export function parseAccentString(s: string): Room['accent'] {
  return parseAccent(s)
}

export function parseRaidPosition(s: string): PlayerGroup['position'] {
  return parsePosition(s)
}

export function parseRoleFromWire(s: string): Player['role'] {
  return parseRole(s)
}

export function belongsToRoom(
  rg: RaidGroup,
  rr: RaidRoom,
  numericRoomId: ReadonlyMap<string, number>,
  _raidRooms: ReadonlyArray<RaidRoom>,
): boolean {
  // Existing snapshots retain the live `rooms.id` in `raid_groups.room_id`,
  // while newer rows may point at the snapshot room. Resolve either relation
  // through the room's stable id so every snapshot group reaches its surface.
  return rg.roomId === Number(rr.id) || rg.roomId === numericRoomId.get(rr.stableId)
}

export function raidRoomToRoom(
  rr: RaidRoom,
  raidGroups: ReadonlyArray<RaidGroup>,
  numericRoomId: ReadonlyMap<string, number>,
  raidRooms: ReadonlyArray<RaidRoom>,
): Room {
  const roomGroupIds = raidGroups
    .filter((g) => g.id === rr.stableId || belongsToRoom(g, rr, numericRoomId, raidRooms))
    .map((g) => g.stableId)
  return {
    id: rr.stableId,
    name: rr.name,
    groupIds: roomGroupIds,
    accent: parseAccent(rr.accent),
    noPlayerLimit: rr.noPlayerLimit,
  }
}

export function groupIdForRaidPlayer(
  rp: RaidPlayer,
  raidGroups: ReadonlyArray<RaidGroup>,
): GroupId {
  const rg = raidGroups.find((g) => g.id === String(rp.groupId))
  return rg?.stableId ?? String(rp.groupId)
}

export function raidGroupToGroup(rg: RaidGroup): PlayerGroup {
  return {
    id: rg.stableId,
    label: rg.label,
    position: parsePosition(rg.position),
    playerIds: rg.playerIds.map(String),
    maxPlayers: rg.maxPlayers,
    collapsed: rg.collapsed,
  }
}

export function raidPlayerToPlayer(
  rp: RaidPlayer,
  builds: ReadonlyArray<ReadonlyArray<string>>,
  raidGroups: ReadonlyArray<RaidGroup>,
): Player {
  return {
    // The planner must address the snapshot row, not its optional live-board
    // source id. This keeps every selected-raid mutation snapshot-scoped.
    id: rp.id,
    discordUserId: rp.discordUserId,
    hasSignedIn: rp.hasSignedIn,
    discordName: rp.discordName,
    guildNickname: rp.guildNickname,
    playerNickname: rp.playerNickname,
    playerAccountName: rp.playerAccountName,
    inGameName: rp.inGameName,
    role: parseRole(rp.role),
    group: groupIdForRaidPlayer(rp, raidGroups),
    mainWeapons: [...rp.mainWeapons],
    altWeapons: [...rp.altWeapons],
    builds,
    guildBuilds: [],
    activeBuildIndex: rp.activeBuildIndex,
    // Raid snapshots don't carry guild-build state — the planner renders a
    // raid-mode read-only view, so the `Player`-shaped carrier gets the
    // "no guild build" sentinel.
    activeGuildBuildIndex: -1,
    tags: [...rp.tags],
  }
}
